import { useMemo } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  FormAutocomplete,
  AutocompleteOption,
} from '@/components/ui/custom/Autocomplete/Autocomplete';
import { FormField, FormItem, FormControl } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CampaignForm } from '@/modules/campaigns/utils';
import { senderFieldPath } from '@/modules/campaigns/utils/senderFieldPath';
import { getComponentsFromTemplate } from '@/modules/whatsapp/utils';
import {
  BroadcastActionComponent,
  WhatsappTemplate,
  WhatsappTemplateComponent,
} from '@/shared/types/broadcast';
import { Option } from '@/shared/types/rules';

interface HeaderMediaSectionProps {
  template: WhatsappTemplate;
  senderPath: string;
  variableOptions: Option[];
  onFocus?: () => void;
}

/**
 * HeaderMediaSection component for configuring IMAGE, VIDEO, DOCUMENT headers
 * Allows users to input media URL or WhatsApp ID
 */
export function HeaderMediaSection({
  template,
  senderPath,
  variableOptions,
  onFocus,
}: HeaderMediaSectionProps) {
  const { t } = useTranslation();
  const { control, setValue, clearErrors } = useFormContext<CampaignForm>();

  const { header } = getComponentsFromTemplate(template);

  // Transform options to use 'category' instead of 'type' for FormAutocomplete
  const options: AutocompleteOption[] = useMemo(() => {
    return variableOptions.map((option) => ({
      value: option.value || '',
      label: option.label || '',
      category: option.type ? t(`auto_complete.group_labels.${option.type}`) : undefined,
    }));
  }, [variableOptions, t]);
  const mediaType = useWatch({
    control,
    name: senderFieldPath(senderPath, 'headerMediaParam.type'),
  }) as string;

  // Helper to check if header is a media header
  const isMediaHeader = (headerComponent: BroadcastActionComponent | WhatsappTemplateComponent) =>
    headerComponent?.format && headerComponent.format.toLowerCase() !== 'text';

  // Only show for media headers (not text or location)
  if (!isMediaHeader(header) || header?.format === 'LOCATION') {
    return null;
  }

  const selectOptions = [
    { label: t('common.link'), value: 'link' },
    { label: 'WhatsApp ID', value: 'id' },
  ];

  const handleTypeChange = (newType: string) => {
    setValue(senderFieldPath(senderPath, 'headerMediaParam.type'), newType);
    setValue(senderFieldPath(senderPath, 'headerMediaParam.value'), '');
    clearErrors(senderFieldPath(senderPath, 'headerMediaParam.value'));
  };

  return (
    <div className="mb-4">
      <p className="mb-2 text-sm leading-5 font-semibold">{t('broadcasts.template_header')}</p>
      <div className="flex gap-2">
        {/* Type selector (ID or Link) */}
        <div className="w-40">
          <FormField
            control={control}
            name={senderFieldPath(senderPath, 'headerMediaParam.type')}
            rules={{ required: t('validation.required') }}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      handleTypeChange(value);
                    }}
                    onOpenChange={(open) => {
                      if (open && onFocus) {
                        onFocus();
                      }
                    }}
                    value={(field.value as string) || ''}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder={t('common.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      {selectOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        {/* Value input (URL or ID) */}
        <div className="flex-1">
          <FormAutocomplete<CampaignForm>
            key={mediaType}
            control={control}
            options={options}
            name={senderFieldPath(senderPath, 'headerMediaParam.value')}
            placeholder={t('broadcasts.url_id_or_variable_placeholder')}
            disabled={!mediaType}
            onCreateNew={(value) => {
              setValue(senderFieldPath(senderPath, 'headerMediaParam.value'), value);
            }}
          />
        </div>
      </div>
    </div>
  );
}
